import { ColumnDef } from "@tanstack/react-table";

export type UsageCreditByTimeRow = Record<string, string | number>;

interface ApiColumn {
  field: string;
  headerName: string;
}

interface ApiColumnGroup {
  groupId: string;
  children: { field: string }[];
}

const parseDateString = (dateString: string): Date => {
  const [day, month, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const buildLeafColumn = (
  column: ApiColumn,
): ColumnDef<UsageCreditByTimeRow> => {
  const base: ColumnDef<UsageCreditByTimeRow> = {
    id: column.field,
    accessorFn: (row) => row[column.field] ?? 0,
    header: column.headerName,
  };

  if (column.field === "Date") {
    return {
      ...base,
      sortingFn: (a, b, columnId) => {
        const v1 = String(a.getValue(columnId));
        const v2 = String(b.getValue(columnId));
        return parseDateString(v1).getTime() - parseDateString(v2).getTime();
      },
    };
  }

  return { ...base, enableSorting: false };
};

export const modifyColumns = (
  columns: ApiColumn[],
  groups?: ApiColumnGroup[],
): ColumnDef<UsageCreditByTimeRow>[] => {
  const columnByField = new Map(columns.map((c) => [c.field, c]));

  if (!groups || groups.length === 0) {
    return columns.map(buildLeafColumn);
  }

  const groupedFields = new Set(
    groups.flatMap((g) => g.children.map((c) => c.field)),
  );

  const standalone = columns
    .filter((c) => !groupedFields.has(c.field))
    .map(buildLeafColumn);

  const grouped: ColumnDef<UsageCreditByTimeRow>[] = groups.map((group) => ({
    id: group.groupId,
    header: group.groupId,
    columns: group.children
      .map((child) => columnByField.get(child.field))
      .filter((c): c is ApiColumn => c !== undefined)
      .map(buildLeafColumn),
  }));

  return [...standalone, ...grouped];
};
